/**
 * Persistencia de parámetros de atribución (UTM + fbclid) desde la landing
 * hasta el checkout, para que Impultienda / Utmify detecten campaña, conjunto
 * y anuncio de cada venta.
 *
 * - Se leen una sola vez, al cargar la página.
 * - Si la URL de entrada trae parámetros, se guardan como un set completo
 *   (nunca se mezclan valores de visitas distintas).
 * - Si la URL no trae parámetros, se conserva lo guardado: la navegación
 *   interna (anclas, scroll, recargas sin query) no pisa la atribución.
 */

export const TRACKING_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'fbclid',
] as const

type TrackingKey = (typeof TRACKING_KEYS)[number]
type TrackingParams = Partial<Record<TrackingKey, string>>

interface StoredTracking {
  params: TrackingParams
  savedAt: number
}

const STORAGE_KEY = 'jabones_tracking_params'
/** Ventana de atribución: 7 días, igual que la de clic por defecto de Meta. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    const probe = '__tp__'
    window.localStorage.setItem(probe, probe)
    window.localStorage.removeItem(probe)
    return window.localStorage
  } catch {
    try {
      return typeof window !== 'undefined' ? window.sessionStorage : null
    } catch {
      return null
    }
  }
}

/** Parámetros presentes en la URL actual (solo valores no vacíos). */
function readFromUrl(): TrackingParams {
  const found: TrackingParams = {}
  if (typeof window === 'undefined') return found
  const search = new URLSearchParams(window.location.search)
  TRACKING_KEYS.forEach((key) => {
    const value = search.get(key)?.trim()
    if (value) found[key] = value
  })
  return found
}

function readStored(): TrackingParams {
  const storage = safeStorage()
  if (!storage) return {}
  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as StoredTracking
    if (!parsed?.params || Date.now() - parsed.savedAt > MAX_AGE_MS) {
      storage.removeItem(STORAGE_KEY)
      return {}
    }
    return parsed.params
  } catch {
    return {}
  }
}

/** Ejecutar una vez al cargar la landing, antes del primer render. */
export function captureTrackingParams(): void {
  const fromUrl = readFromUrl()
  if (Object.keys(fromUrl).length === 0) return

  const storage = safeStorage()
  if (!storage) return
  try {
    const payload: StoredTracking = { params: fromUrl, savedAt: Date.now() }
    storage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    /* almacenamiento lleno o bloqueado: el fallback en vivo cubre el caso */
  }
}

/** Parámetros vigentes: los de la URL actual o, si no hay, los guardados. */
export function getTrackingParams(): TrackingParams {
  const fromUrl = readFromUrl()
  return Object.keys(fromUrl).length > 0 ? fromUrl : readStored()
}

/**
 * Agrega los parámetros de atribución a una URL de checkout sin quitar los
 * que ya tenga ni duplicarlos. Sin parámetros, devuelve la URL intacta.
 */
export function withTrackingParams(baseUrl: string): string {
  const params = getTrackingParams()
  const entries = Object.entries(params) as [TrackingKey, string][]
  if (entries.length === 0) return baseUrl

  try {
    const url = new URL(baseUrl)
    entries.forEach(([key, value]) => {
      if (!url.searchParams.has(key)) url.searchParams.set(key, value)
    })
    return url.toString()
  } catch {
    return baseUrl
  }
}
